/* eslint-disable react/jsx-no-useless-fragment */
import React, { forwardRef } from 'react';
import { Checkbox, Skeleton } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { AssetsTable } from '@components/Tables';
import AssetsEmptyState from '@components/Assets/AssetsEmptyState';
import { AssetItemDto, CampaignItemDto } from '@api/Api';
import AssetCard from '@components/Assets/AssetCard';
import { DraggableList } from '@components/DraggableList/DraggableList';
import { DraggableItem } from '@components/DraggableList';
import { reorganizeAssetList } from '@redux/actions/assetsAction';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCombineAssets } from '@helpers/useCombineAssets';
import { showReorderRequiresCustomSortHint } from '@helpers/showReorderRequiresCustomSortHint';

const AssetsListSkeleton = forwardRef<HTMLDivElement, { isTileView: boolean }>(
  function AssetsListSkeleton({ isTileView }, ref) {
    return (
      <div
        ref={ref}
        className="assets_card_container"
        style={!isTileView ? { height: 56 } : {}}
      >
        <div className="assets_card_container__preview">
          {isTileView ? (
            <>
              <div className="asset-image-container">
                <Skeleton.Input style={{ width: '100%', height: 152 }} />
              </div>

              <Skeleton.Input
                style={{ width: '100%', height: 40, marginTop: 11 }}
              />
            </>
          ) : (
            <Skeleton.Input style={{ width: '100%', height: 56 }} />
          )}
        </div>
      </div>
    );
  }
);

function AssetsList({
  campaign,
  folderId,
  searchQuery,
  assetsList,
  displayAssetsCount,
  isTileView,
  page,
  countToFetch,
  canReorganize,
  canCombine,
  canShare = true,
  isShowCompany = true,
  listLoading,
  selectedAssets = [],
  setSelectedAssets,
  showSelectAll = true
}: {
  campaign?: CampaignItemDto;
  folderId?: string;
  searchQuery: string | null;
  assetsList: {
    node: AssetItemDto;
    cursor: string;
  }[];
  displayAssetsCount: number;
  isTileView: boolean;
  page: string;
  countToFetch: number;
  canReorganize?: boolean;
  canCombine?: boolean;
  canShare?: boolean;
  isShowCompany?: boolean;
  listLoading?: boolean;
  selectedAssets?: {
    node: AssetItemDto;
    cursor: string;
  }[];
  setSelectedAssets?: (
    ids: string[],
    assets: { node: AssetItemDto; cursor: string }[],
    isAllSelect: boolean
  ) => void;
  showSelectAll?: boolean;
}) {
  const dispatch = useTypedDispatch();

  const onCheckboxChange = (v: CheckboxValueType[]) => {
    if (!setSelectedAssets) return;
    const assets = v
      .map((el) => {
        const asset = assetsList.find((asset) => asset.node.id === el);
        if (asset) return asset;
        return { node: null, cursor: '' };
      })
      .filter((item) => !!item.node) as {
      node: AssetItemDto;
      cursor: string;
    }[];

    setSelectedAssets(
      assets.map((x) => x.node.id),
      assets,
      false
    );
  };

  const combineAssets = useCombineAssets(assetsList);

  const listPaddingStyle = {
    display: 'block' as const,
    paddingRight: 34,
    paddingLeft: 34,
    marginRight: -34,
    marginLeft: -34
  };

  const listContent = (
    <div className="assets-container">
      {displayAssetsCount === 0 ? (
        <AssetsEmptyState
          campaign={campaign}
          folderId={folderId}
          isSearchEmptyState={page !== 'all'}
        />
      ) : (
        <>
          {page !== 'recent' && searchQuery && !assetsList.length ? (
            <AssetsEmptyState
              campaign={campaign}
              folderId={folderId}
              isSearchEmptyState={true}
            />
          ) : (
            <>
              {isTileView && (
                <DraggableList
                  className="assets-container-list"
                  gap={32}
                  onReorder={(e) => {
                    if (!canReorganize) {
                      showReorderRequiresCustomSortHint();
                      return;
                    }
                    dispatch(reorganizeAssetList(e));
                  }}
                  onDropInside={(e) => {
                    combineAssets(e.item.id, e.insideItem.id);
                  }}
                >
                  {!listLoading && assetsList.length
                    ? assetsList
                        .concat(new Array(countToFetch).fill(''))
                        .map(({ node }, idx) => {
                          if (!node) {
                            return (
                              <AssetsListSkeleton
                                key={idx}
                                isTileView={isTileView}
                              />
                            );
                          }
                          return (
                            <DraggableItem
                              key={node.id}
                              id={node.id}
                              type="asset"
                              droppable={!!canCombine}
                              data-testid={`asset-item-${idx}`}
                              className="draggable-item-asset"
                            >
                              <AssetCard
                                asset={node}
                                selectedOne={selectedAssets?.length > 0}
                                page={page}
                                canShare={canShare}
                                isShowCompany={isShowCompany}
                              />
                            </DraggableItem>
                          );
                        })
                    : new Array(displayAssetsCount)
                        .fill('')
                        .map((el, idx) => (
                          <AssetsListSkeleton isTileView={true} key={idx} />
                        ))}
                </DraggableList>
              )}
              {!isTileView && (
                <AssetsTable
                  assetsList={assetsList}
                  countToFetch={countToFetch}
                  countToLoad={new Array(displayAssetsCount).fill('') || []}
                  page={page}
                  canShare={canShare}
                  listLoading={listLoading}
                  onReorder={(e) => dispatch(reorganizeAssetList(e))}
                  canReorganize={canReorganize}
                  selectedAssets={selectedAssets}
                  setSelectedAssets={setSelectedAssets}
                  assetsCount={displayAssetsCount}
                  showSelectAll={showSelectAll}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {isTileView ? (
        <Checkbox.Group
          value={selectedAssets.map((el) => el?.node?.id || '')}
          style={listPaddingStyle}
          onChange={onCheckboxChange}
        >
          {listContent}
        </Checkbox.Group>
      ) : (
        <div style={listPaddingStyle}>{listContent}</div>
      )}
    </>
  );
}

export default AssetsList;
